import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, ROLES } from '../../context/AppContext';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { CaseQuickActions } from '../../components/shared/CaseQuickActions';
import { STATUS_CONFIG, CASE_STATUSES } from '../../data/mockData';
import { fetchCases, fetchClients, fetchProducts, fetchUsers, deleteCase } from '../../api';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  Filter,
  Download,
  Upload,
  UserPlus,
  AlertCircle,
  Eye,
  Trash2,
  CheckCircle2,
  XCircle,
  Mail,
  FileText,
  FolderArchive,
  Phone,
  History,
  MessageCircle,
} from 'lucide-react';
import { format } from 'date-fns';

const priorityColors = {
  high: 'bg-red-50 border border-red-200 text-red-700 font-semibold text-xs',
  medium: 'bg-amber-50 border border-amber-200 text-amber-700 font-semibold text-xs',
  low: 'bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold text-xs',
};

export default function CaseManagement() {
  const {
    currentRole,
    currentUserId,
    hasPermission,
    caseStatusOverrides,
    submitDeletionRequest,
    caseReturnRequestsList,
    recommendReturnRequest,
    approveReturnRequest,
    rejectReturnRequest,
  } = useApp();
  const isClient = currentRole === ROLES.CLIENT;
  const navigate = useNavigate();
  const canDirectDelete = [ROLES.ADMIN].includes(currentRole);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [showNewCaseDialog, setShowNewCaseDialog] = useState(false);

  // Return requests actions states
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(''); // recommend, recommend_reject, approve, reject
  const [actionRemark, setActionRemark] = useState('');

  const handleOpenActionDialog = (req, type) => {
    setSelectedRequest(req);
    setActionType(type);
    setActionRemark('');
    setShowActionDialog(true);
  };

  const handleConfirmAction = () => {
    if (!selectedRequest) return;
    const activeUser = users.find(u => u.id === currentUserId) || { name: 'Admin', role: currentRole };
    const userName = activeUser.name;
    const roleLabel = activeUser.role === ROLES.ADMIN ? 'Admin' : (activeUser.role === ROLES.SUPREME_MANAGER ? 'Supreme Manager' : 'Manager');

    if (actionType === 'recommend') {
      recommendReturnRequest(selectedRequest.id, roleLabel, 'Approved', actionRemark, userName);
      toast.success('Case return recommended successfully!');
    } else if (actionType === 'recommend_reject') {
      recommendReturnRequest(selectedRequest.id, roleLabel, 'Rejected', actionRemark, userName);
      toast.warning('Case return request rejected!');
    } else if (actionType === 'approve') {
      approveReturnRequest(selectedRequest.id, actionRemark, userName);
      toast.success('Case return approved! Case returned to pool.');
    } else if (actionType === 'reject') {
      rejectReturnRequest(selectedRequest.id, actionRemark, userName);
      toast.warning('Case return request rejected!');
    }

    setShowActionDialog(false);
    setSelectedRequest(null);
    setActionRemark('');
  };

  // Deletion modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caseForDeletion, setCaseForDeletion] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteRemark, setDeleteRemark] = useState('');
  const [attachedFileName, setAttachedFileName] = useState('');

  // Assignment bulk action states
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');

  useEffect(() => {
    async function loadData() {
      try {
        const [casesRes, clientsRes, productsRes, usersRes] = await Promise.all([
          fetchCases().catch(e => { console.warn(e); return []; }),
          fetchClients().catch(e => { console.warn(e); return []; }),
          fetchProducts().catch(e => { console.warn(e); return []; }),
          fetchUsers().catch(e => { console.warn(e); return []; })
        ]);
        
        const rawCases = Array.isArray(casesRes) ? casesRes : [];

        // Note: The cases endpoint already returns clientName & productName via JOIN in PHP
        // But we adapt it into the nested structure expected by the datatable if necessary:
        const normalizedCases = rawCases.map(c => {
          return {
            ...c,
            address: c.address || 'House No. 15, Sadar Bazar, Nagpur - 440001',
            applicantPhone: c.applicantPhone || '+91 98765 43210',
            applicantEmail: c.applicantEmail || 'applicant@email.com',
            client: c.client_id ? { id: c.client_id, name: c.clientName, logo: '' } : (c.client || { id: '', name: 'N/A' }),
            product: c.product_id ? { id: c.product_id, name: c.productName } : (c.product || { id: '', name: 'N/A' }),
            assignedTo: c.assignedTo_id ? { id: c.assignedTo_id, name: c.assignedToName, avatar: '' } : (c.assignedTo || null),
          };
        });
        setCases(normalizedCases);

        if (clientsRes && Array.isArray(clientsRes)) setClients(clientsRes);
        if (productsRes && Array.isArray(productsRes)) setProducts(productsRes);
        if (usersRes && Array.isArray(usersRes)) setUsers(usersRes);
      } catch (error) {
        console.error("Failed to fetch CaseManagement data", error);
        setCases([]);
      }
    }
    loadData();
  }, []);

  const [showRemovalDialog, setShowRemovalDialog] = useState(false);
  const [removalReason, setRemovalReason] = useState('');
  const [casesToRemove, setCasesToRemove] = useState([]);

  const [showManualVerifyDialog, setShowManualVerifyDialog] = useState(false);
  const [manualVerifyData, setManualVerifyData] = useState({ result: 'verified', notes: '' });

  // Filter assignees according to hierarchy limitations
  const getEligibleAssignees = () => {
    // Hierarchy limitations: Admin -> Supreme -> Manager -> Vendor/FE
    if (currentRole === ROLES.ADMIN) {
      return users.filter(u => [ROLES.SUPREME_MANAGER, ROLES.MANAGER, ROLES.VENDOR, ROLES.FIELD_EXECUTIVE].includes(u.role));
    } else if (currentRole === ROLES.SUPREME_MANAGER || currentRole === ROLES.QUALITY_CHECKER) {
      return users.filter(u => [ROLES.MANAGER, ROLES.VENDOR, ROLES.FIELD_EXECUTIVE].includes(u.role));
    } else if (currentRole === ROLES.MANAGER) {
      return users.filter(u => [ROLES.VENDOR, ROLES.FIELD_EXECUTIVE].includes(u.role));
    } else if (currentRole === ROLES.VENDOR) {
      return users.filter(u => u.role === ROLES.FIELD_EXECUTIVE);
    }
    return users.filter(u => u.role === ROLES.FIELD_EXECUTIVE);
  };

  const processedCases = cases.map(c => ({
    ...c,
    status: caseStatusOverrides[c.id] || c.status
  }));

  const filteredCases = processedCases.filter(c => {
    if (isClient && c.client_id !== 'CLT001' && c.client?.id !== 'CLT001') return false;
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (clientFilter !== 'all' && c.client_id !== clientFilter) return false;
    return true;
  });

  const columns = [
    {
      key: 'refNumber',
      header: 'Case ID',
      sortable: true,
      render: (row) => {
        const verificationType = row.verificationType || row.product?.name || row.productName || 'Residence Verification';
        return (
          <div>
            <div className="flex items-center gap-2">
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/cases/${row.id}`);
                }}
                className="font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
              >
                {row.refNumber || row.id}
              </span>
              {row.pendingDeletion && <Badge variant="destructive" className="h-4 text-[9px] uppercase px-1">Rem Req</Badge>}
            </div>
            <p className="text-xs text-slate-500 font-medium">Product: {row.product?.name || row.productName || 'Address Verification'}</p>
            <p className="text-[11px] text-slate-400 italic">Verification: {verificationType}</p>
          </div>
        );
      },
    },
    {
      key: 'client',
      header: 'Client',
      render: (row) => {
        const cName = row.client?.name || row.clientName || 'Client';
        return (
          <div className="flex items-center gap-2">
            <Avatar className="w-7 h-7">
              <AvatarImage src={row.client?.logo} />
              <AvatarFallback className="text-xs">{cName.charAt(0)}</AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium text-slate-700">{cName}</span>
          </div>
        );
      },
    },
    {
      key: 'applicantName',
      header: 'Applicant',
      sortable: true,
      render: (row) => (
        <div className="space-y-1">
          <p className="text-sm font-bold text-slate-800">{row.applicantName || 'N/A'}</p>
          <p className="text-xs text-slate-500 font-normal leading-relaxed break-words whitespace-normal">{row.address || 'N/A'}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusBadge
          status={STATUS_CONFIG[row.status]?.color || 'neutral'}
          label={STATUS_CONFIG[row.status]?.label || row.status || 'Active'}
        />
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => {
        const pKey = (row.priority || 'medium').toLowerCase();
        return (
          <Badge variant="outline" className={priorityColors[pKey] || priorityColors.medium}>
            {pKey.charAt(0).toUpperCase() + pKey.slice(1)}
          </Badge>
        );
      },
    },
    {
      key: 'assignedTo',
      header: 'Assigned To',
      render: (row) => row.assignedTo ? (
        <div className="flex items-center gap-2">
          <Avatar className="w-6 h-6">
            <AvatarImage src={row.assignedTo.avatar} />
            <AvatarFallback className="text-xs">{(row.assignedTo.name || 'U').charAt(0)}</AvatarFallback>
          </Avatar>
          <span className="text-sm text-slate-700">{row.assignedTo.name || 'Assigned User'}</span>
        </div>
      ) : (
        <span className="text-slate-500 text-sm">Unassigned</span>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      sortable: true,
      render: (row) => {
        let dateDisplay = 'N/A';
        if (row.dueDate) {
          try {
            const d = new Date(row.dueDate);
            if (!isNaN(d.getTime())) dateDisplay = format(d, 'MMM dd, yyyy');
          } catch {
            dateDisplay = row.dueDate;
          }
        }
        return (
          <span className="text-sm text-slate-500 font-medium">
            {dateDisplay}
          </span>
        );
      },
    },
    {
      key: 'quickActions',
      header: 'Quick Actions',
      render: (row) => <CaseQuickActions caseData={row} />,
    },
  ];

  if (isClient) {
    columns.push({
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <Button
          variant="outline"
          size="sm"
          className="text-red-600 border-red-200 hover:bg-red-50 gap-1 bg-transparent"
          onClick={(e) => {
            e.stopPropagation(); // Avoid triggering row click navigate
            setCaseForDeletion(row);
            setShowDeleteModal(true);
          }}
        >
          <Trash2 className="w-3.5 h-3.5" />
          Request Delete
        </Button>
      )
    });
  }

  const bulkActions = [];
  if (!isClient) {
    bulkActions.push({ key: 'assign', label: 'Assign / Reassign', icon: UserPlus });
    bulkActions.push({ key: 'unassign', label: 'Unassign', icon: AlertCircle });
  }
  bulkActions.push({ key: 'export', label: 'Export', icon: Download });

  if (isClient) {
    bulkActions.push({ key: 'request_delete', label: 'Request Removal', icon: Trash2 });
  }

  if (canDirectDelete) {
    bulkActions.push({ key: 'direct_delete', label: 'Remove Case', icon: Trash2 });
    bulkActions.push({ key: 'approve_delete', label: 'Approve Rem', icon: CheckCircle2 });
    bulkActions.push({ key: 'reject_delete', label: 'Reject Rem', icon: XCircle });
  }

  if ([ROLES.SUPREME_MANAGER, ROLES.MANAGER, ROLES.QUALITY_CHECKER].includes(currentRole) && !isClient) {
    bulkActions.push({ key: 'manual_verification', label: 'Manual Verify', icon: CheckCircle2 });
  }

  const exportCasesToCsv = (casesList) => {
    const target = casesList && casesList.length > 0 ? casesList : filteredCases;
    if (!target || target.length === 0) {
      toast.error('No cases available to export');
      return;
    }
    const headers = ['Case ID', 'Client', 'Product', 'Verification Type', 'Applicant Name', 'Phone', 'Address', 'Status', 'Priority', 'Assigned To', 'Due Date'];
    const rows = target.map(c => [
      `"${c.refNumber || c.id || ''}"`,
      `"${c.client?.name || c.clientName || ''}"`,
      `"${c.product?.name || c.productName || ''}"`,
      `"${c.verificationType || ''}"`,
      `"${c.applicantName || ''}"`,
      `"${c.applicantPhone || ''}"`,
      `"${(c.address || '').replace(/"/g, '""')}"`,
      `"${(c.status || '').toUpperCase()}"`,
      `"${(c.priority || '').toUpperCase()}"`,
      `"${c.assignedTo?.name || 'Unassigned'}"`,
      `"${c.dueDate || ''}"`
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `AD_Ventures_Cases_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Successfully exported ${target.length} case(s) to CSV!`);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="case-management-page">
      {/* Page Header (Sticky Action Bar) */}
      <div className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur-md pb-4 pt-2 mb-6 flex items-center justify-between border-b border-slate-200">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Case Management</h1>
          <p className="text-sm text-slate-500 mt-1">Manage and track all verification cases</p>
        </div>
        <div className="flex items-center gap-3">
          {hasPermission('cases', 'create') && currentRole !== ROLES.VENDOR && (
            <Button
              variant="outline"
              className="bg-white border-slate-200 text-slate-700 hover:text-slate-900 hover:bg-slate-50 gap-2"
              onClick={() => navigate('/import-export')}
              data-testid="import-cases-btn"
            >
              <Upload className="w-4 h-4" />
              {isClient ? "Import Cases" : "Import"}
            </Button>
          )}
          <Button
            variant="outline"
            className="bg-white border-slate-200 text-slate-700 hover:text-slate-900 hover:bg-slate-50 gap-2 font-medium"
            onClick={() => exportCasesToCsv(filteredCases)}
            data-testid="export-cases-btn"
          >
            <Download className="w-4 h-4 text-blue-600" />
            Export
          </Button>
          {hasPermission('cases', 'create') && currentRole !== ROLES.VENDOR && (
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2 shadow-lg shadow-blue-500/10"
              onClick={() => setShowNewCaseDialog(true)}
              data-testid="new-case-btn"
            >
              <Plus className="w-4 h-4" />
              New Case
            </Button>
          )}
        </div>
      </div>

      {/* Tab Switcher if Admin/Manager/SM */}
      {[ROLES.ADMIN, ROLES.SUPREME_MANAGER, ROLES.MANAGER].includes(currentRole) ? (
        <Tabs defaultValue="cases" className="w-full">
          <TabsList className="bg-white border border-slate-200 p-1 w-full max-w-xs flex mb-6">
            <TabsTrigger value="cases" className="flex-1 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 font-semibold text-sm">
              Active Cases
            </TabsTrigger>
            <TabsTrigger value="return-requests" className="flex-1 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 font-semibold text-sm">
              Return Requests
              {caseReturnRequestsList.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-2 bg-rose-500 text-white font-bold h-5 px-1.5 min-w-5 inline-flex items-center justify-center rounded-full text-[10px]">
                  {caseReturnRequestsList.filter(r => r.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cases" className="space-y-6">
            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-500">Filters:</span>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-white border-slate-200" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.entries(CASE_STATUSES).map(([key, value]) => (
                    <SelectItem key={key} value={value}>
                      {STATUS_CONFIG[value]?.label || value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isClient && (
                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger className="w-40 bg-white border-slate-200" data-testid="client-filter">
                    <SelectValue placeholder="Client" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {(statusFilter !== 'all' || clientFilter !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setStatusFilter('all');
                    setClientFilter('all');
                  }}
                  className="text-slate-500 hover:text-slate-900"
                >
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Cases Table */}
            <DataTable
              columns={columns}
              data={filteredCases}
              onRowClick={(row) => navigate(`/cases/${row.id}`)}
              bulkActions={bulkActions}
              onBulkAction={(actionKey, selectedRows) => {
                if (actionKey === 'assign') {
                  setSelectedCaseIds(selectedRows);
                  setShowAssignDialog(true);
                } else if (actionKey === 'unassign') {
                  setCases(prev => prev.map(c => selectedRows.includes(c.id) ? { ...c, assignedTo: null } : c));
                } else if (actionKey === 'request_delete') {
                  const rowToReq = filteredCases.find(c => selectedRows.includes(c.id));
                  if (rowToReq) {
                    setCaseForDeletion(rowToReq);
                    setShowDeleteModal(true);
                  }
                } else if (actionKey === 'direct_delete') {
                  setCasesToRemove(selectedRows);
                  setShowRemovalDialog(true);
                } else if (actionKey === 'approve_delete') {
                  if (window.confirm(`Permanently remove ${selectedRows.length} case(s)?`)) {
                    Promise.all(selectedRows.map(id => deleteCase(id, true)))
                      .then(() => {
                        setCases(prev => prev.filter(c => !selectedRows.includes(c.id)));
                        toast.success(`${selectedRows.length} case(s) permanently deleted from database`);
                      })
                      .catch(err => {
                        console.error("Delete error:", err);
                        toast.error("Failed to delete case(s) from database");
                      });
                  }
                } else if (actionKey === 'reject_delete') {
                  setCases(prev => prev.map(c => selectedRows.includes(c.id) ? { ...c, pendingDeletion: false } : c));
                } else if (actionKey === 'manual_verification') {
                  setSelectedCaseIds(selectedRows);
                  setShowManualVerifyDialog(true);
                } else if (actionKey === 'export') {
                  const toExport = filteredCases.filter(c => selectedRows.includes(c.id));
                  exportCasesToCsv(toExport.length > 0 ? toExport : filteredCases);
                }
              }}
              searchPlaceholder="Search cases..."
              pageSize={10}
            />
          </TabsContent>

          <TabsContent value="return-requests" className="mt-6">
            <Card className="crm-card border-slate-200 bg-white p-6 rounded-3xl shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Case Return Requests</h3>
                  <p className="text-xs text-slate-500">Review and approve requests from Field Executives to return cases for Non-Working Areas.</p>
                </div>
                <span className="text-xs font-semibold text-slate-655 bg-slate-50 border border-slate-100 px-2.5 py-0.5 rounded-full">
                  Total Requests: {caseReturnRequestsList.length}
                </span>
              </div>

              {caseReturnRequestsList.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">
                  <AlertCircle className="w-10 h-10 mx-auto text-slate-350 mb-2" />
                  No return requests raised yet.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-100">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold">
                      <tr>
                        <th className="px-4 py-3">Request ID</th>
                        <th className="px-4 py-3">Case ID</th>
                        <th className="px-4 py-3">Applicant Name</th>
                        <th className="px-4 py-3">Requested By</th>
                        <th className="px-4 py-3">Reason</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {caseReturnRequestsList.map((req) => (
                        <tr key={req.id} className="hover:bg-slate-50/20 font-medium">
                          <td className="px-4 py-3.5 font-bold text-slate-800">{req.id}</td>
                          <td className="px-4 py-3.5 text-blue-650 font-bold hover:underline cursor-pointer" onClick={() => navigate(`/cases/${req.caseId}`)}>{req.refNumber}</td>
                          <td className="px-4 py-3.5 text-slate-800 font-bold">{req.applicantName}</td>
                          <td className="px-4 py-3.5">
                            <span className="text-slate-700 font-semibold">{req.requestedBy?.name}</span>
                            <span className="text-[10px] text-slate-400 block">{(req.requestedBy?.role || '').replace('_', ' ').toUpperCase()}</span>
                          </td>
                          <td className="px-4 py-3.5 text-slate-600">
                            <span className="font-semibold">{req.reason}</span>
                            <p className="text-[10px] text-slate-400 mt-0.5 max-w-[200px] truncate" title={req.comments}>{req.comments}</p>
                          </td>
                          <td className="px-4 py-3.5 text-slate-500">
                            {req.date ? format(new Date(req.date), 'dd-MMM-yyyy hh:mm a') : 'N/A'}
                          </td>
                          <td className="px-4 py-3.5">
                            <StatusBadge
                              status={
                                req.status === 'approved' ? 'success' :
                                req.status === 'rejected' ? 'danger' :
                                req.status === 'recommended' ? 'info' : 'warning'
                              }
                              label={
                                req.status === 'recommended' ? 'Recommended' :
                                req.status.charAt(0).toUpperCase() + req.status.slice(1)
                              }
                            />
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-1.5">
                              {req.status === 'pending' && (
                                <>
                                  {[ROLES.MANAGER, ROLES.SUPREME_MANAGER].includes(currentRole) && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 border-blue-200 text-blue-600 hover:bg-blue-50 text-[10px] font-bold bg-transparent"
                                        onClick={() => handleOpenActionDialog(req, 'recommend')}
                                      >
                                        Recommend
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 border-red-100 text-rose-600 hover:bg-red-50 text-[10px] font-bold bg-transparent"
                                        onClick={() => handleOpenActionDialog(req, 'recommend_reject')}
                                      >
                                        Reject
                                      </Button>
                                    </>
                                  )}
                                  {currentRole === ROLES.ADMIN && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 border-emerald-200 text-emerald-600 hover:bg-emerald-50 text-[10px] font-bold bg-transparent"
                                        onClick={() => handleOpenActionDialog(req, 'approve')}
                                      >
                                        Approve
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 border-red-100 text-rose-650 hover:bg-red-50 text-[10px] font-bold bg-transparent"
                                        onClick={() => handleOpenActionDialog(req, 'reject')}
                                      >
                                        Reject
                                      </Button>
                                    </>
                                  )}
                                </>
                              )}

                              {req.status === 'recommended' && currentRole === ROLES.ADMIN && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 border-emerald-250 text-emerald-600 hover:bg-emerald-50 text-[10px] font-bold bg-transparent"
                                    onClick={() => handleOpenActionDialog(req, 'approve')}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 border-red-105 text-rose-650 hover:bg-red-50 text-[10px] font-bold bg-transparent"
                                    onClick={() => handleOpenActionDialog(req, 'reject')}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}

                              {['approved', 'rejected'].includes(req.status) && (
                                <span className="text-[10px] text-slate-405 italic font-bold">Resolved</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        <>
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-500">Filters:</span>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-white border-slate-200" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(CASE_STATUSES).map(([key, value]) => (
                  <SelectItem key={key} value={value}>
                    {STATUS_CONFIG[value]?.label || value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isClient && (
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger className="w-40 bg-white border-slate-200" data-testid="client-filter">
                  <SelectValue placeholder="Client" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {(statusFilter !== 'all' || clientFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter('all');
                  setClientFilter('all');
                }}
                className="text-slate-500 hover:text-slate-900"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Cases Table */}
          <DataTable
            columns={columns}
            data={filteredCases}
            onRowClick={(row) => navigate(`/cases/${row.id}`)}
            bulkActions={bulkActions}
            onBulkAction={(actionKey, selectedRows) => {
              if (actionKey === 'assign') {
                setSelectedCaseIds(selectedRows);
                setShowAssignDialog(true);
              } else if (actionKey === 'unassign') {
                setCases(prev => prev.map(c => selectedRows.includes(c.id) ? { ...c, assignedTo: null } : c));
              } else if (actionKey === 'request_delete') {
                const rowToReq = filteredCases.find(c => selectedRows.includes(c.id));
                if (rowToReq) {
                  setCaseForDeletion(rowToReq);
                  setShowDeleteModal(true);
                }
              } else if (actionKey === 'direct_delete') {
                setCasesToRemove(selectedRows);
                setShowRemovalDialog(true);
              } else if (actionKey === 'approve_delete') {
                if (window.confirm(`Permanently remove ${selectedRows.length} case(s)?`)) {
                  Promise.all(selectedRows.map(id => deleteCase(id, true)))
                    .then(() => {
                      setCases(prev => prev.filter(c => !selectedRows.includes(c.id)));
                      toast.success(`${selectedRows.length} case(s) permanently deleted from database`);
                    })
                    .catch(err => {
                      console.error("Delete error:", err);
                      toast.error("Failed to delete case(s) from database");
                    });
                }
              } else if (actionKey === 'reject_delete') {
                setCases(prev => prev.map(c => selectedRows.includes(c.id) ? { ...c, pendingDeletion: false } : c));
              } else if (actionKey === 'manual_verification') {
                setSelectedCaseIds(selectedRows);
                setShowManualVerifyDialog(true);
              } else if (actionKey === 'export') {
                const toExport = filteredCases.filter(c => selectedRows.includes(c.id));
                exportCasesToCsv(toExport.length > 0 ? toExport : filteredCases);
              }
            }}
            searchPlaceholder="Search cases..."
            pageSize={10}
          />
        </>
      )}

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Assign Cases</DialogTitle>
            <DialogDescription className="text-slate-500">
              Assign {selectedCaseIds.length} selected case(s) to a user based on your role hierarchy.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Assignee</Label>
            <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
              <SelectTrigger className="mt-2 bg-white border-slate-200">
                <SelectValue placeholder="Select user..." />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {getEligibleAssignees().map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name} ({u.role.replace('_', ' ')})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="border-slate-200">
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!selectedAssignee}
              onClick={() => {
                const assigneeObj = users.find(u => u.id === selectedAssignee);
                if (assigneeObj) {
                  setCases(prev => prev.map(c => selectedCaseIds.includes(c.id) ? { ...c, assignedTo: assigneeObj } : c));
                }
                setShowAssignDialog(false);
                setSelectedAssignee('');
              }}
            >
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Case Dialog */}
      <Dialog open={showNewCaseDialog} onOpenChange={setShowNewCaseDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Create New Case</DialogTitle>
            <DialogDescription className="text-slate-500">
              Fill in the details to create a new verification case.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="client">Client</Label>
              <Select>
                <SelectTrigger className="bg-slate-50/50 border-slate-200">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="product">Product</Label>
              <Select>
                <SelectTrigger className="bg-slate-50/50 border-slate-200">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="applicantName">Applicant Name</Label>
              <Input id="applicantName" className="bg-slate-50/50 border-slate-200" placeholder="Enter applicant name" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="address">Address</Label>
              <Input id="address" className="bg-slate-50/50 border-slate-200" placeholder="Enter address to verify" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="priority">Priority</Label>
              <Select>
                <SelectTrigger className="bg-slate-50/50 border-slate-200">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCaseDialog(false)} className="border-slate-200">
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowNewCaseDialog(false)}>
              Create Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Removal Dialog */}
      <Dialog open={showRemovalDialog} onOpenChange={(open) => {
        if (!open) { setRemovalReason(''); setCasesToRemove([]); }
        setShowRemovalDialog(open);
      }}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Case Removal</DialogTitle>
            <DialogDescription className="text-slate-500">
              Please provide a reason for rejecting and removing the selected case(s). This will notify the client.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-slate-700">Rejection Reason <span className="text-red-500">*</span></Label>
            <Input
              value={removalReason}
              onChange={(e) => setRemovalReason(e.target.value)}
              placeholder="e.g. Invalid documents provided..."
              className="mt-2 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemovalDialog(false)} className="bg-transparent border-slate-300 text-slate-700 hover:text-slate-900 hover:bg-slate-100">
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white font-semibold"
              onClick={async () => {
                try {
                  await Promise.all(casesToRemove.map(id => deleteCase(id, false)));
                  setCases(prev => prev.filter(c => !casesToRemove.includes(c.id)));
                  setShowRemovalDialog(false);
                  toast.success(`Selected case(s) successfully removed from database.`);
                } catch (err) {
                  console.error("Removal error:", err);
                  toast.error("Failed to remove cases.");
                }
              }}
            >
              Confirm Removal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Verification Dialog */}
      <Dialog open={showManualVerifyDialog} onOpenChange={setShowManualVerifyDialog}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Manual Verification</DialogTitle>
            <DialogDescription className="text-slate-500">
              Update the verification status manually for {selectedCaseIds.length} case(s).
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Verification Result</Label>
              <Select value={manualVerifyData.result} onValueChange={(val) => setManualVerifyData({ ...manualVerifyData, result: val })}>
                <SelectTrigger className="bg-white border-slate-200">
                  <SelectValue placeholder="Select result..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="verified">Verified (Positive)</SelectItem>
                  <SelectItem value="qc_rejected">Rejected (Negative)</SelectItem>
                  <SelectItem value="revisit">Revisit Required</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes / Comments</Label>
              <Input
                value={manualVerifyData.notes}
                onChange={(e) => setManualVerifyData({ ...manualVerifyData, notes: e.target.value })}
                className="bg-white border-slate-200"
                placeholder="Add verification notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualVerifyDialog(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => {
              setCases(prev => prev.map(c => selectedCaseIds.includes(c.id) ? { ...c, status: manualVerifyData.result } : c));
              setShowManualVerifyDialog(false);
              window.alert('Manual verification applied to cases.');
            }}>
              Submit Verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Request Case Deletion Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Request Case Deletion
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Submit a formal request to delete this case. Deletion requires administrative review and approval.
            </DialogDescription>
          </DialogHeader>

          {caseForDeletion && (
            <div className="space-y-4 py-3">
              {/* Case Info Section */}
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100 text-xs space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-slate-500">Case ID:</span>
                  <span className="font-semibold text-slate-800">{caseForDeletion.refNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Applicant:</span>
                  <span className="font-semibold text-slate-800">{caseForDeletion.applicantName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Product:</span>
                  <span className="font-semibold text-slate-800">{caseForDeletion.product.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Verification Type:</span>
                  <span className="font-semibold text-slate-800">{caseForDeletion.verificationType || caseForDeletion.product.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Current Status:</span>
                  <span className="capitalize font-semibold text-slate-800">
                    {STATUS_CONFIG[caseForDeletion.status]?.label || caseForDeletion.status}
                  </span>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-slate-700">Delete Reason <span className="text-red-500">*</span></Label>
                  <Select value={deleteReason} onValueChange={setDeleteReason}>
                    <SelectTrigger className="bg-white border-slate-200 text-xs h-9">
                      <SelectValue placeholder="Select reason..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="Duplicate Case">Duplicate Case</SelectItem>
                      <SelectItem value="Wrong Details">Wrong Details</SelectItem>
                      <SelectItem value="Client Request">Client Request</SelectItem>
                      <SelectItem value="Uploaded By Mistake">Uploaded By Mistake</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-slate-700">Remark / Explanation <span className="text-red-500">*</span></Label>
                  <textarea
                    className="w-full min-h-[70px] rounded-md border border-slate-200 p-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 bg-white"
                    placeholder="Provide detailed justification..."
                    value={deleteRemark}
                    onChange={(e) => setDeleteRemark(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Supporting Document <span className="text-slate-400 font-normal">(Optional)</span></Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      id="doc-upload"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          setAttachedFileName(e.target.files[0].name);
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-slate-200 hover:bg-slate-50 text-xs h-8 bg-transparent"
                      onClick={() => document.getElementById('doc-upload')?.click()}
                    >
                      <Upload className="w-3.5 h-3.5 mr-1" />
                      Choose File
                    </Button>
                    {attachedFileName ? (
                      <span className="text-[11px] text-emerald-600 font-medium truncate max-w-[200px]">
                        {attachedFileName}
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-400">
                        Supports PDF, JPG, PNG
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowDeleteModal(false);
                setCaseForDeletion(null);
                setDeleteReason('');
                setDeleteRemark('');
                setAttachedFileName('');
              }}
              className="border-slate-200 text-xs h-8 bg-transparent"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (!deleteReason || !deleteRemark) {
                  window.alert('Please select a reason and enter remarks.');
                  return;
                }
                const request = {
                  id: `DEL${Date.now()}`,
                  caseId: caseForDeletion.id,
                  refNumber: caseForDeletion.refNumber,
                  applicantName: caseForDeletion.applicantName,
                  product: caseForDeletion.product.name,
                  verificationType: caseForDeletion.verificationType || caseForDeletion.product.name,
                  currentStatus: caseForDeletion.status,
                  clientName: caseForDeletion.client?.name || 'HDFC Bank',
                  requestedBy: 'Neha Joshi (Client)',
                  reason: deleteReason,
                  remarks: deleteRemark,
                  attachmentName: attachedFileName || null,
                  requestDate: new Date().toISOString(),
                  status: 'pending',
                  adminRemark: ''
                };
                submitDeletionRequest(request);
                setShowDeleteModal(false);
                setCaseForDeletion(null);
                setDeleteReason('');
                setDeleteRemark('');
                setAttachedFileName('');
                window.alert('Case deletion request submitted successfully! Case status updated to Deletion Pending.');
              }}
              className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold h-8"
              disabled={!deleteReason || !deleteRemark}
            >
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Request Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-md text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2 font-bold">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              {actionType === 'recommend' && 'Recommend Case Return'}
              {actionType === 'recommend_reject' && 'Reject Return Recommendation'}
              {actionType === 'approve' && 'Approve Case Return'}
              {actionType === 'reject' && 'Reject Case Return'}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              {actionType === 'recommend' && 'Provide comments to recommend this case return to Admin.'}
              {actionType === 'recommend_reject' && 'Provide reasons to reject this case return request.'}
              {actionType === 'approve' && 'Approving will revert the case status and unassign it, returning it to the pool.'}
              {actionType === 'reject' && 'Provide remarks for rejecting this case return request.'}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4 py-3">
              <div className="bg-slate-50 p-4 border border-slate-150 rounded-2xl text-xs space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500 font-semibold">Case Reference:</span>
                  <span className="font-bold text-slate-800">{selectedRequest.refNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-semibold">Applicant Name:</span>
                  <span className="font-bold text-slate-800">{selectedRequest.applicantName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-semibold">Requested By:</span>
                  <span className="font-bold text-slate-800">{selectedRequest.requestedBy?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-semibold">FE Reason:</span>
                  <span className="font-medium text-slate-700">{selectedRequest.reason}</span>
                </div>
                <div className="flex flex-col gap-1 pt-1 border-t border-slate-200">
                  <span className="text-slate-500 font-semibold">FE Comment:</span>
                  <span className="font-medium text-slate-650 bg-white p-2 rounded-lg border border-slate-100 block whitespace-pre-wrap">{selectedRequest.comments}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Remarks / Explanation <span className="text-red-500">*</span></Label>
                <textarea
                  placeholder="Enter remarks or justification (mandatory)..."
                  value={actionRemark}
                  onChange={(e) => setActionRemark(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 p-3 text-xs bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500/25 min-h-[90px] text-slate-900"
                />
              </div>
            </div>
          )}

          <DialogFooter className="border-t border-slate-100 pt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setShowActionDialog(false); setSelectedRequest(null); }} className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl text-xs px-4 py-2 bg-transparent">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              disabled={!actionRemark.trim()}
              className={`text-white rounded-xl text-xs px-4 py-2 font-bold ${
                ['recommend_reject', 'reject'].includes(actionType)
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
